import { useMemo } from 'react';
import { useGetBrandDataownersQuery, useGetBrandLocationsQuery } from '../../api/brandApi';
import { createContext, useContext, useEffect, useState } from 'react';
import { DataownerBasic, LocationOverview } from '@dap/memberadmin/types';
import { useSelectedBrandKey } from './brandSelectors';
import { useUser } from '../../redux/user/userSelectors';
import { RoleType } from '@dap/common/types';
import { InformationModelType } from '@dap/memberadmin/types';

type BrandDetailsContextType = {
  locations: Array<LocationOverview>;
  dataowners: Array<DataownerBasic>;
  dataownersForUser: Array<DataownerBasic>;
};

const BrandDetailsContext = createContext<BrandDetailsContextType | undefined>(undefined);

type BrandDetailsProviderProps = {
  children: React.ReactNode;
};

/**
 * Fetches the locations and dataowners for the current brand
 */
export function BrandDetailsProvider({ children }: BrandDetailsProviderProps) {
  const userData = useUser();
  const brandKey = useSelectedBrandKey();

  const getBrandLocationsQueryState = useGetBrandLocationsQuery(brandKey, { skip: !brandKey });
  const [locations, setLocations] = useState<Array<LocationOverview>>([]);

  const getBrandDataownersQueryState = useGetBrandDataownersQuery(brandKey, { skip: !brandKey });
  const [dataowners, setDataowners] = useState<Array<DataownerBasic>>([]);

  useEffect(() => {
    if (getBrandLocationsQueryState.status === 'fulfilled' && getBrandLocationsQueryState.data) {
      setLocations(getBrandLocationsQueryState.data);
    }
  }, [getBrandLocationsQueryState.status, getBrandLocationsQueryState.data]);

  useEffect(() => {
    if (getBrandDataownersQueryState.status === 'fulfilled' && getBrandDataownersQueryState.data) {
      setDataowners(getBrandDataownersQueryState.data);
    }
  }, [getBrandDataownersQueryState.status, getBrandDataownersQueryState.data]);

  /**
   * Get all the dataowners the user has access to
   *
   * Iterate roles and return any dataowner that is either a dataowner the user has the dataowner role "medlemadmin" on,
   * or the dataowner of a location the user has the location role "lokasjonsadministrator" on.
   */
  const dataownersForUser = useMemo<DataownerBasic[]>(() => {
    if (!userData || !dataowners || !locations) {
      return [];
    }

    // Filter roles to include the relevant roles
    const roles = userData.roles.filter((role) => {
      if (role.type === InformationModelType.Dataowner && role.role.key === RoleType.Medlemsadmin) {
        return true;
      }

      if (
        role.type === InformationModelType.Location &&
        role.role.key === RoleType.Lokasjonsadministrator
      ) {
        return true;
      }

      return false;
    });

    // Map roles to dataowners, and return unique dataowners
    return (
      roles
        .map((role) => {
          if (role.type === InformationModelType.Dataowner) {
            return dataowners.find((dataowner) => dataowner.key === role.orgUnitKey);
          }

          const dataownerForLocation = locations.find(
            (location) => location.key === role.orgUnitKey
          )?.dataownerKey;

          if (dataownerForLocation) {
            return dataowners.find((dataowner) => dataowner.key === dataownerForLocation);
          }

          return undefined;
        })
        // Remove undefined dataowner
        .filter((dataowner): dataowner is DataownerBasic => dataowner !== undefined)
        // Ensure we only have unique dataowner by comparing the uuid of each dataowner
        .filter(
          (dataowner, index, self) => index === self.findIndex((t) => t.uuid === dataowner.uuid)
        )
    );
  }, [userData, dataowners, locations]);

  const brandDetailsContextValue: BrandDetailsContextType = {
    locations,
    dataowners,
    dataownersForUser,
  };

  return (
    <BrandDetailsContext.Provider value={brandDetailsContextValue}>
      {children}
    </BrandDetailsContext.Provider>
  );
}

export const useBrandDetails = (): BrandDetailsContextType => {
  const context = useContext(BrandDetailsContext);

  if (!context) {
    throw new Error('Cannot get brand');
  }

  return context;
};
